// Preschool invoicing data: Firestore listeners, the manager's direct edits, and the invoicing callables.
//
// The invoice maths and prefill are shared with the Cloud Functions (functions/src/preschool/preschoolInvoicing.ts).

import { addDoc, collection, deleteDoc, doc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase-config";
import { preschoolStub } from "./preschoolInvoicesStub";
import {
  toPreschoolChild,
  type ChildRecord,
  type FundingPayment,
  type PreschoolChild,
  type PreschoolInvoice,
  type PreschoolInvoiceInput,
  type PreschoolTerm,
  type RegistrationRecord,
} from "../../../preschool.bee-happy.club_functions/functions/src/preschool/preschoolInvoicing";

export * from "../../../preschool.bee-happy.club_functions/functions/src/preschool/preschoolInvoicing";
export * from "../../../preschool.bee-happy.club_functions/functions/src/preschool/preschoolCalendar";
import {
  academicYearOfTerm,
  termOpenDays,
  termSpan,
  type PreschoolCalendar,
} from "../../../preschool.bee-happy.club_functions/functions/src/preschool/preschoolCalendar";
import { defaultTerm } from "../../../preschool.bee-happy.club_functions/functions/src/preschool/preschoolInvoicing";

type Unsubscribe = () => void;

/** `npm run dev` uses in-memory mock data (preschoolInvoicesStub.ts) instead of Firestore and the callables. */
const DEV = import.meta.env.DEV;

export const watchInvoices = (onData: (invoices: PreschoolInvoice[]) => void, onError: (error: Error) => void): Unsubscribe =>
  DEV ? preschoolStub().watchInvoices(onData) : onSnapshot(collection(db, "preschoolInvoices"),
    (snapshot) => onData(snapshot.docs.map((d) => d.data() as PreschoolInvoice)), onError);

export const watchTerms = (onData: (terms: PreschoolTerm[]) => void, onError: (error: Error) => void): Unsubscribe =>
  DEV ? preschoolStub().watchTerms(onData) : onSnapshot(collection(db, "preschoolTerms"),
    (snapshot) => onData(snapshot.docs.map((d) => ({ ...(d.data() as Omit<PreschoolTerm, "id">), id: d.id }))), onError);

/** Children that are not ignored, joined with their registrations. */
export const watchChildren = (onData: (children: PreschoolChild[]) => void, onError: (error: Error) => void): Unsubscribe => {
  if (DEV) return preschoolStub().watchChildren(onData);
  let children: ChildRecord[] | null = null;
  let registrations: Map<string, RegistrationRecord> | null = null;
  const emit = () => {
    if (!children || !registrations) return;
    onData(children.filter((c) => !c.ignore).map((c) => toPreschoolChild(c, c.registrationId ? registrations!.get(c.registrationId) : undefined))
      .sort((a, b) => a.name.localeCompare(b.name)));
  };
  const stopChildren = onSnapshot(collection(db, "children"), (snapshot) => {
    children = snapshot.docs.map((d) => ({ ...(d.data() as ChildRecord), id: d.id }));
    emit();
  }, onError);
  const stopRegistrations = onSnapshot(collection(db, "registrations"), (snapshot) => {
    registrations = new Map(snapshot.docs.map((d) => [d.id, d.data() as RegistrationRecord]));
    emit();
  }, onError);
  return () => { stopChildren(); stopRegistrations(); };
};

export const watchFundingPayments = (onData: (payments: FundingPayment[]) => void, onError: (error: Error) => void): Unsubscribe =>
  DEV ? preschoolStub().watchFundingPayments(onData) : onSnapshot(collection(db, "preschoolFundingPayments"),
    (snapshot) => onData(snapshot.docs.map((d) => ({ ...(d.data() as Omit<FundingPayment, "id">), id: d.id }))), onError);

export const addFundingPayment = async (payment: Omit<FundingPayment, "id">) => {
  if (DEV) return preschoolStub().addFundingPayment(payment);
  await addDoc(collection(db, "preschoolFundingPayments"), payment);
};

export const deleteFundingPayment = async (id: string) => {
  if (DEV) return preschoolStub().deleteFundingPayment(id);
  await deleteDoc(doc(db, "preschoolFundingPayments", id));
};

export const watchCalendars = (onData: (calendars: PreschoolCalendar[]) => void, onError: (error: Error) => void): Unsubscribe =>
  DEV ? preschoolStub().watchCalendars(onData) : onSnapshot(collection(db, "preschoolCalendar"),
    (snapshot) => onData(snapshot.docs.map((d) => ({ ...(d.data() as Omit<PreschoolCalendar, "id">), id: d.id }))), onError);

export const saveCalendar = async (calendar: PreschoolCalendar) => {
  if (DEV) return preschoolStub().saveCalendar(calendar);
  const { id, ...data } = calendar;
  await setDoc(doc(db, "preschoolCalendar", id), JSON.parse(JSON.stringify(data)));
};

/** A term as invoicing sees it: dates and weeks from the calendar when it has them, else the usual weeks. */
export const termFromCalendars = (calendars: PreschoolCalendar[], termId: string): { term: PreschoolTerm; open: string[] | null } => {
  const fallback = defaultTerm(termId);
  const calendar = calendars.find((c) => c.id === academicYearOfTerm(termId));
  const open = calendar ? termOpenDays(calendar, termId) : null;
  const span = open ? termSpan(open) : null;
  if (!open || !span) return { term: fallback, open: null };
  return { term: { ...fallback, startDate: span.start, endDate: span.end, weeks: Math.round((open.length / 5) * 10) / 10 }, open };
};

export const updateInvoice = async (ref: string, change: { paid?: boolean; paidDate?: string | null; cancelled?: boolean }) => {
  if (DEV) return preschoolStub().updateInvoice(ref, change);
  await updateDoc(doc(db, "preschoolInvoices", ref), change);
};

export const saveTerm = async (term: PreschoolTerm) => {
  if (DEV) return preschoolStub().saveTerm(term);
  const { id, ...data } = term;
  await setDoc(doc(db, "preschoolTerms", id), data);
};

export interface IssueResult { ref: string; totalPence: number; draftId: string | null; error: string | null }

export const issueInvoice = async (input: PreschoolInvoiceInput): Promise<IssueResult> =>
  DEV ? preschoolStub().issueInvoice(input)
    : (await httpsCallable<PreschoolInvoiceInput, IssueResult>(functions, "issuePreschoolInvoice")(input)).data;

export const reissueInvoice = async (ref: string, input: PreschoolInvoiceInput) =>
  DEV ? preschoolStub().reissueInvoice(ref, input)
    : (await httpsCallable<{ ref: string; input: PreschoolInvoiceInput }, { ref: string; totalPence: number; draftId: string }>(
      functions, "reissuePreschoolInvoice")({ ref, input })).data;

export const createReceipt = async (ref: string, paymentDate: string) =>
  DEV ? preschoolStub().createReceipt(ref, paymentDate)
    : (await httpsCallable<{ ref: string; paymentDate: string }, { ref: string; draftId: string }>(
      functions, "createPreschoolReceipt")({ ref, paymentDate })).data;

/** The stored PDF of an issued invoice (or its receipt) as an object URL; null in dev, where nothing is stored. */
export const loadStoredPdf = async (ref: string, which: "invoice" | "receipt" = "invoice"): Promise<string | null> => {
  if (DEV) return null;
  const { base64 } = (await httpsCallable<{ ref: string; which: string }, { base64: string; filename: string }>(
    functions, "getPreschoolPdf")({ ref, which })).data;
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

/** Today as YYYY-MM-DD in the browser's local time. */
export const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

export const addDays = (isoDate: string, days: number) => {
  const date = new Date(`${isoDate}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};
